import { useEffect, useState } from "react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { History, Trash2, Share2, Copy, ExternalLink, Play, X, GitCompareArrows, Zap, ZapOff, Sparkles } from "lucide-react";
import { fmtRelative } from "@/lib/format";
import BrandCompareDialog from "@/components/Brand/BrandCompareDialog";
import BrandCompareDiffDialog from "@/components/Brand/BrandCompareDiffDialog";

/** Iter 174 · Compare Snapshot browser. Lists saved snapshots, re-opens or
 *  deletes them, exposes share links for shareable snapshots. */

export default function CompareSnapshotsDialog({ onClose }) {
  const { activeWorkspaceId: wsId } = useAuth();
  const [rows, setRows] = useState(null);
  const [replayIds, setReplayIds] = useState(null);
  const [diffSnap, setDiffSnap] = useState(null);

  const load = async () => {
    try {
      const r = await api.get(`/workspaces/${wsId}/brand-compare/snapshots`);
      setRows(r.data.rows || []);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load snapshots"));
      setRows([]);
    }
  };
  useEffect(() => { if (wsId) load(); }, [wsId]);

  const toggleAutopilot = async (snap) => {
    const on = !snap.autopilot;
    try {
      await api.post(
        `/workspaces/${wsId}/brand-compare/snapshots/${snap.id}/autopilot`,
        { enabled: on });
      setRows((rs) => (rs || []).map((s) => s.id === snap.id
        ? { ...s, autopilot: on } : s));
      toast.success(on
        ? "Autopilot ON · Monday-morning summary emails scheduled"
        : "Autopilot OFF");
    } catch (e) { toast.error(apiErrorMessage(e, "Toggle failed")); }
  };

  const runAutopilotNow = async () => {
    if (!confirm("Run autopilot now? Summaries email to workspace owners immediately and baselines are re-captured.")) return;
    const t = toast.loading("Running autopilot…");
    try {
      const r = await api.post(
        `/workspaces/${wsId}/brand-compare/autopilot/run-now`);
      toast.success(
        `Processed ${r.data.processed} · emailed ${r.data.sent} recipient${r.data.sent === 1 ? "" : "s"}`,
        { id: t });
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Run failed"), { id: t }); }
  };

  const remove = async (sid) => {
    try {
      await api.delete(`/workspaces/${wsId}/brand-compare/snapshots/${sid}`);
      toast.success("Snapshot deleted");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  const toggleShare = async (snap, on) => {
    try {
      const r = await api.patch(
        `/workspaces/${wsId}/brand-compare/snapshots/${snap.id}`,
        { shareable: on });
      setRows((rs) => (rs || []).map((s) => s.id === snap.id
        ? { ...s, ...r.data } : s));
      toast.success(on ? "Share link enabled" : "Share link revoked");
    } catch (e) { toast.error(apiErrorMessage(e, "Update failed")); }
  };

  const copyLink = async (token) => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/compare/${token}`);
      toast.success("Link copied");
    } catch { toast.error("Could not copy"); }
  };

  return (
    <>
      <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-hidden flex flex-col"
                          data-testid="compare-snapshots-dialog">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <DialogTitle className="flex items-center gap-2">
                <History className="w-5 h-5" /> Saved brand comparisons
              </DialogTitle>
              <div className="ml-auto">
                <Button size="sm" variant="outline"
                          className="rounded-none h-8 border-violet-500/50 text-violet-700 hover:bg-violet-50"
                          onClick={runAutopilotNow}
                          data-testid="compare-autopilot-run-now">
                  <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Run autopilot now
                </Button>
              </div>
            </div>
            <DialogDescription>
              Replay any saved snapshot, share externally via public link, or
              enable <b>Autopilot</b> to auto-summarize & email weekly changes.
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto -mx-1 px-1">
            {rows === null ? (
              <div className="py-8 text-center text-sm text-muted-foreground">Loading…</div>
            ) : rows.length === 0 ? (
              <div className="py-10 text-center text-sm text-muted-foreground border border-dashed border-border">
                No saved snapshots yet. Open a Compare view and click <b>Save snapshot</b>.
              </div>
            ) : (
              <div className="divide-y divide-border" data-testid="compare-snapshots-list">
                {rows.map((s) => (
                  <div key={s.id} className="py-3 flex items-start gap-3"
                         data-testid={`compare-snapshot-row-${s.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-sm font-semibold">{s.name}</span>
                        <span className="text-[10px] text-muted-foreground">
                          {fmtRelative(s.created_at)} · {s.created_by_name || "—"}
                        </span>
                        {s.share_token && (
                          <span className="text-[9px] uppercase tracking-widest border border-primary/40 bg-primary/5 text-primary px-1.5 py-0.5">
                            SHARED
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-muted-foreground mt-0.5">
                        {(s.brand_names || []).join(" · ")}
                      </div>
                      {s.notes && (
                        <div className="text-xs mt-1 whitespace-pre-wrap max-w-2xl">{s.notes}</div>
                      )}
                    </div>
                    <Button size="sm" variant="outline" className="rounded-none h-8"
                              onClick={() => setReplayIds(s.brand_ids)}
                              data-testid={`compare-snapshot-replay-${s.id}`}>
                      <Play className="w-3 h-3 mr-1" /> Open
                    </Button>
                    <Button size="sm" variant="outline" className="rounded-none h-8"
                              onClick={() => setDiffSnap(s)}
                              data-testid={`compare-snapshot-diff-${s.id}`}
                              title="Diff vs. latest live state">
                      <GitCompareArrows className="w-3 h-3 mr-1" /> Diff
                    </Button>
                    <Button size="sm" variant="outline"
                              className={`rounded-none h-8 ${s.autopilot ? "border-violet-500 bg-violet-50 text-violet-700" : ""}`}
                              onClick={() => toggleAutopilot(s)}
                              data-testid={`compare-snapshot-autopilot-${s.id}`}
                              title={s.autopilot
                                ? "Autopilot ON — Monday-morning summary emails scheduled"
                                : "Enable Monday-morning autopilot emails"}>
                      {s.autopilot
                        ? <><Zap className="w-3 h-3 mr-1" /> Autopilot</>
                        : <><ZapOff className="w-3 h-3 mr-1" /> Autopilot</>}
                    </Button>
                    {s.share_token ? (
                      <>
                        <Button size="sm" variant="outline" className="rounded-none h-8"
                                  onClick={() => copyLink(s.share_token)}
                                  data-testid={`compare-snapshot-copy-${s.id}`}>
                          <Copy className="w-3 h-3 mr-1" /> Copy link
                        </Button>
                        <Button size="sm" variant="ghost" className="rounded-none h-8 text-amber-700"
                                  onClick={() => toggleShare(s, false)}
                                  data-testid={`compare-snapshot-revoke-${s.id}`}
                                  title="Revoke share link">
                          <X className="w-3 h-3" />
                        </Button>
                      </>
                    ) : (
                      <Button size="sm" variant="outline" className="rounded-none h-8"
                                onClick={() => toggleShare(s, true)}
                                data-testid={`compare-snapshot-share-${s.id}`}>
                        <Share2 className="w-3 h-3 mr-1" /> Share
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" className="rounded-none h-8 text-rose-600"
                              onClick={() => remove(s.id)}
                              data-testid={`compare-snapshot-delete-${s.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      {replayIds && (
        <BrandCompareDialog wsId={wsId} ids={replayIds}
                                onClose={() => setReplayIds(null)} />
      )}
      {diffSnap && (
        <BrandCompareDiffDialog wsId={wsId} snapshotId={diffSnap.id}
                                     snapshotName={diffSnap.name}
                                     onClose={() => setDiffSnap(null)} />
      )}
    </>
  );
}
